import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Space,
  Input,
  Tooltip,
  notification,
} from "antd";
import { InfoCircleOutlined } from '@ant-design/icons';
import debounce from "lodash/debounce";
import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import {
  setReconciliationPageNo,
  setReconciliationSearchParam,
  setReconciliationSelectedTab,
  setReconciliationIsShowFilter,
} from "shared/redux/reconciliation-reducer";
import API_SERVICE from "shared/services/api-service";
import CheckableTag from "antd/lib/tag/CheckableTag";
import blackFilterIcon from "../../assets/images/blackFilter.svg";
import blackRefreshIcon from "../../assets/images/blackReferesh.svg";
import { setScreenHeader } from "shared/redux/common-reducer";
import ReconciliationFilter from "./ReconciliationFilter";
import { FaSearch } from "react-icons/fa";
import ReconciliationDisbursements from "./ReconciliationDisbursements";
import ReconciliationInvoices from "./ReconciliationInvoices";
import ReconciliationIssues from "./ReconciliationIssues";
import ReconciliationReconciled from "./ReconciliationReconciled";
import ReconciliationDiscrepancies from "./ReconciliationDiscrepancies";
import ReconciliationAdjustedDiscrepancies from "./ReconciliationAdjustedDiscrepancies";

type Props = {};

const Reconciliation: React.FunctionComponent<Props> = () => {
  const [loadingDropdown, setLoadingDropdown] = useState(false);
  const [filteredValues, setFilteredValues] = useState({});
  const [reconciliationFilterValues, setReconciliationFilterValues] = useState({
    lenders: [] as { key: string; value: string }[],
    loanProducts: [] as { key: string; value: string }[],
  });

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const { appColors } = useSelector(
    (state: RootState) => state.common
  );
  const { selectedTab, isShowFilter } = useSelector(
    (state: RootState) => state.reconciliation
  );

  const disbursementsRef = useRef<any>(null);
  const reconciledRef = useRef<any>(null);
  const filterDropdownBootstrapDoneRef = useRef(false);

  const getFilterDropdownValues = useCallback(() => {
    setLoadingDropdown(true);
    API_SERVICE.getFilterDropdownValues("DISBURSED")
      .then((response) => {
        if (response?.data?.payload) {
          const { lenders, loanProducts } = response.data.payload;
          setReconciliationFilterValues({
            lenders: lenders || [],
            loanProducts: loanProducts || [],
          });
        }
      })
      .catch((error) => {
        notification.error({
          message: "Error",
        });
      })
      .finally(() => {
        setLoadingDropdown(false);
      });
  }, []);

  const onReconcileDataSequenceComplete = useCallback(() => {
    if (filterDropdownBootstrapDoneRef.current) return;
    filterDropdownBootstrapDoneRef.current = true;
    getFilterDropdownValues();
  }, [getFilterDropdownValues]);

  const handleApplyFilter = useCallback(
    (values: any) => {
      // Only include the specified filter keys
      const updatedValues: Record<string, any> = {
        lenderId: values.lenderName,
        productId: values.product,
        disbursedDateFrom: values.disbursedDate?.[0]?.format("YYYY-MM-DD"),
        disbursedDateTo: values.disbursedDate?.[1]?.format("YYYY-MM-DD"),
      };

      // Handle lender disbursed date for RECONCILED, DISCREPANCIES and ADJUSTED_DISCREPANCIES tabs
      if ((selectedTab === "RECONCILED" || selectedTab === "DISCREPANCIES" || selectedTab === "ADJUSTED_DISCREPANCIES") && values.lenderDisbursedDate) {
        updatedValues.lenderDisbursedDateFrom = values.lenderDisbursedDate?.[0]?.format("YYYY-MM-DD");
        updatedValues.lenderDisbursedDateTo = values.lenderDisbursedDate?.[1]?.format("YYYY-MM-DD");
      }
      
      // Clean up undefined values
      const cleanedValues = Object.fromEntries(
        Object.entries(updatedValues).filter(([_, v]) => v !== undefined && v !== null)
      );
      
      setFilteredValues(cleanedValues);
      dispatch(setReconciliationPageNo(1));
    },
    [dispatch, selectedTab]
  );

  useEffect(() => {
    dispatch(setScreenHeader({ screenTitle: "Reconciliation" }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setReconciliationSearchParam(null));
    form.resetFields();
  }, [
    selectedTab,
    dispatch,
    form,
  ]);

  const debouncedSearch = debounce((e: any) => {
    const value = e.target.value ? e.target.value : null;
    dispatch(setReconciliationPageNo(1));
    dispatch(setReconciliationSearchParam(value));
  }, 400);

  const handleSearch = debouncedSearch;

  const handleTabChange = (key: string) => {
    dispatch(setReconciliationSelectedTab(key));
    
    // Hide filter panel when switching to INVOICES tab
    if (key === "INVOICES" && isShowFilter) {
      dispatch(setReconciliationIsShowFilter(false));
    }
  };

  const handleFilterIconClick = () => {
    dispatch(setReconciliationIsShowFilter(!isShowFilter));
  };

  const renderTabs = () => {
    return [
      {
        key: "DISBURSEMENTS",
        value: "Disbursements",
        total: "",
      },
      {
        key: "RECONCILED",
        value: "Reconciled",
        total: "",
      },
      {
        key: "ISSUES",
        value: "Issues",
        total: "",
      },
      {
        key: "DISCREPANCIES",
        value: "Open Discrepancies",
        total: "",
      },
      {
        key: "ADJUSTED_DISCREPANCIES",
        value: "Adjusted Discrepancies",
        total: "",
      },
      {
        key: "INVOICES",
        value: "Invoices",
        total: "",
      },
    ];
  };

  const [listCase] = useState(renderTabs());

  const customStyles = { "--bgColor": appColors?.appPrimaryColor ?? "" } as React.CSSProperties;
  const exportBtnPrimaryStyles = {
    "--btnColor": appColors?.appPrimaryColor ?? "",
    width: "100px",
  } as React.CSSProperties;

  return (
    <div className="reconciliation-content-box content-box">
      <Form form={form}>
        <Row gutter={0} className="reduced-width-row">
          <Col className="filter-bar search-container" span={16} style={{ flex: 1, maxWidth: '60%' }}>
            <Form.Item
              name="searchBox"
              style={{ flex: 1, marginBottom: "0px" }}
            >
              <Input
                size="large"
                className="search-box uniform-spacing"
                placeholder="Search"
                onChange={handleSearch}
                allowClear
                prefix={<FaSearch />}
              />
            </Form.Item>
          </Col>
          <Col className="filter-bar actions-container" span={8} style={{ display: 'flex', alignItems: 'center', maxWidth: '40%' }}>
            <Space size={"middle"} className="uniform-spacing">
              {selectedTab !== "INVOICES" && (
                <Tooltip title="Filter">
                  <Button
                    type="primary"
                    className="filter-btn flex-center uniform-spacing"
                    onClick={handleFilterIconClick}
                  >
                    <img src={blackFilterIcon} alt="" />
                    <div 
                      className="badge-green"
                      hidden={
                        !Object.keys(filteredValues).some(
                          (key: any) => filteredValues[`${key}`]?.length > 0
                        )
                      }
                    />
                  </Button>
                </Tooltip>
              )}

              <Tooltip title="Refresh">
                <Button
                  type="primary"
                  className="refresh-button uniform-spacing"
                  onClick={() => {
                    form.setFieldsValue({ searchBox: "" });
                    filterForm.resetFields();
                    setFilteredValues({});
                    dispatch(setReconciliationIsShowFilter(false));
                    dispatch(setReconciliationPageNo(1));
                    dispatch(setReconciliationSearchParam(null));
                    if (selectedTab === "DISBURSEMENTS") {
                      disbursementsRef.current?.refreshData();
                    } else if (selectedTab === "RECONCILED") {
                      reconciledRef.current?.refreshData();
                    }
                  }}
                >
                  <img
                    src={blackRefreshIcon}
                    alt=""
                    className="refresh-icon-adjust"
                  />
                </Button>
              </Tooltip>

              {(selectedTab === "DISBURSEMENTS" || selectedTab === "RECONCILED") && (
                <>
                  <Button
                    className="export-button dynamic-btn-primary uniform-spacing"
                    style={exportBtnPrimaryStyles}
                    type="primary"
                    onClick={() => {
                      if (selectedTab === "DISBURSEMENTS") {
                        disbursementsRef.current?.handleExport();
                      } else if (selectedTab === "RECONCILED") {
                        reconciledRef.current?.handleExport();
                      }
                    }}
                  >
                    Export
                  </Button>

                  <Button
                    className="export-button dynamic-btn-primary uniform-spacing"
                    style={exportBtnPrimaryStyles}
                    type="primary"
                    onClick={() => {
                      if (selectedTab === "DISBURSEMENTS") {
                        disbursementsRef.current?.handleImport();
                      } else if (selectedTab === "RECONCILED") {
                        reconciledRef.current?.handleImport();
                      }
                    }}
                  >
                    Import
                  </Button>
                  
                  <Tooltip title="Use the export and import function for bulk processing offline">
                    <InfoCircleOutlined style={{ minWidth: 'auto', color: appColors?.appPrimaryColor ?? '' }}/>
                  </Tooltip>
                </>
              )}
            </Space>
          </Col>
        </Row>
      </Form>
      <Row>
        {isShowFilter && selectedTab !== "INVOICES" && (
          <Col className="filter-panel-width">
            <ReconciliationFilter
              filterForm={filterForm}
              loadingDropdown={loadingDropdown}
              reconciliationFilterValues={reconciliationFilterValues}
              onApplyFilter={handleApplyFilter}
              onClose={() => dispatch(setReconciliationIsShowFilter(false))}
              selectedTab={selectedTab}
            />
          </Col>
        )}
      </Row>
      <Row>
        <Col>
          {listCase
            .filter((item) => item)
            ?.map((item: any, i: number) => (
              <CheckableTag
                className="payout-checkable"
                style={customStyles}
                key={i}
                checked={item.key === selectedTab}
                onChange={(checked) => handleTabChange(item.key)}
              >
                {item.value}
              </CheckableTag>
            ))}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {selectedTab === "DISBURSEMENTS" && (
            <ReconciliationDisbursements
              ref={disbursementsRef}
              filterValues={filteredValues}
              onReconcileDataSequenceComplete={onReconcileDataSequenceComplete}
            />
          )}
          {selectedTab === "INVOICES" && <ReconciliationInvoices filterValues={filteredValues} />}
          {selectedTab === "ISSUES" && (
            <ReconciliationIssues filterValues={filteredValues} onReconcileDataSequenceComplete={onReconcileDataSequenceComplete} />
          )}
          {selectedTab === "RECONCILED" && (
            <ReconciliationReconciled
              ref={reconciledRef}
              filterValues={filteredValues}
              onReconcileDataSequenceComplete={onReconcileDataSequenceComplete}
            />
          )}
          {selectedTab === "DISCREPANCIES" && (
            <ReconciliationDiscrepancies filterValues={filteredValues} onReconcileDataSequenceComplete={onReconcileDataSequenceComplete} />
          )}
          {selectedTab === "ADJUSTED_DISCREPANCIES" && (
            <ReconciliationAdjustedDiscrepancies
              filterValues={filteredValues}
              onReconcileDataSequenceComplete={onReconcileDataSequenceComplete}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Reconciliation;
